import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { TableComponent } from './table.component';
import { TablePageEvent, TableRowReorderEvent } from 'primeng/table';
import { InputSwitchChangeEvent } from 'primeng/inputswitch';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { StatusElement } from './interfaces';

@Injectable({
  providedIn: 'root',
})
export class TablePresenter<T, Z, X, F> {
  isEnableActionButtonsSubject = new BehaviorSubject<boolean>(false);
  isEnableActionButtons$ = this.isEnableActionButtonsSubject.asObservable();
  view!: TableComponent<T, Z, X, F>;

  setView(view: TableComponent<T, Z, X, F>): void {
    this.view = view;

    this.view.data$
      .pipe(filter((data): data is T[] => !!data))
      .subscribe((value) => {
        this.view.data = [...value]
        this.view.cdr.detectChanges();

        setTimeout(() => {
            this.view.first = (this.view.currentPage-1)*this.view.rows; // Restaura el valor después de que los datos se actualicen
        });
      });

    this.validateActionButtons();
  }

  isEnableButtonActions(): boolean {
    return !!this.view.actionEdit ||
      !!this.view.actionEditExpanded ||
      !!this.view.actionRemove ||
      !!this.view.actionActivate ||
      !!this.view.actionDesactive ||
      !!this.view.actionRemoveExpanded;
  }

  calculateColumns(): number {
    let quantityColumns = this.view.columns.length ?? 0;

    if (this.isEnableButtonActions())
      quantityColumns += 1;

    if (this.view.enableRowCheckbox)
      quantityColumns += 1;

    return quantityColumns;
  }

  hasFilterableColumns(): boolean {
    return this.view.columns.some(col => col.filterable);
  }

  editElement(element: T|Z) : void {
    this.view.actionEditTriggered.emit(element);
  }

  removeElement(element: T) : void {
    this.view.actionRemoveTriggered.emit(element);
  }

  statusElement({ element, standalone, isActivate }: StatusElement<T>): void {
    if ((!!isActivate && !standalone) || (!!standalone && !!isActivate))
      this.activateElement(element);
    else if ((!isActivate && !standalone) || (!!standalone && !isActivate))
      this.deactivateElement(element);
  }

  activateElement(element: T) : void {
    this.view.actionActivateTriggered.emit(element);
  }

  deactivateElement(element: T) : void {
    this.view.actionDeactivateTriggered.emit(element);
  }

  onRowReorder(event: TableRowReorderEvent): void {
    this.view.actionReOrderTriggered.emit(event);
  }

  onRowEdit(): void {
    this.view.actionRowEditTriggered.emit(this.view.data);
  }

  onSelectionChange(_checkeds: T[]): void {
    if (this.view.maxSelections && (this.view.selectedRows.length > this.view.maxSelections)) {
      setTimeout(() => {
        const lastSelected = this.view.selectedRows.slice(-(this.view.maxSelections as number));
        this.view.selectedRows = !!lastSelected ? lastSelected : [];
        this.view.actionRowSelectTriggered.emit(this.view.selectedRows);
      });
    } else {
      this.view.actionRowSelectTriggered.emit(this.view.selectedRows);
    }

    this.view.selectedRowsChange.emit(this.view.selectedRows);
  } 

  validateActionButtons(): void {
    if (this.isEnableButtonActions())
      this.isEnableActionButtonsSubject.next(true);
  }

  isArray(value: T): boolean {
    return Array.isArray(value);
  }

  hasMultipleDynamicValues<T extends Record<string, T>>(
    obj: T,
    path: string,
    parentPath: string,
    checkAll: boolean = true,
    expectedValue?: unknown
  ): boolean {
    const dynamicArray = this.getDynamicValue(obj, path);

    // Si no es un array o está vacío, retorna false
    if (!Array.isArray(dynamicArray) || dynamicArray.length === 0)
      return false;

    // Si no hay parentPath, verificamos los valores directos del array
    if (!parentPath) {
      return checkAll 
        ? dynamicArray.every(item => Boolean(item) === (expectedValue !== undefined ? expectedValue : true))
        : dynamicArray.some(item => Boolean(item) === (expectedValue !== undefined ? expectedValue : true));
    }

    // Si hay parentPath, verificamos la propiedad en cada elemento
    return checkAll
      ? dynamicArray.every(item => {
          const value = this.getDynamicValue(item, parentPath);
          return expectedValue !== undefined ? value === expectedValue : Boolean(value);
        })
      : dynamicArray.some(item => {
          const value = this.getDynamicValue(item, parentPath);
          return expectedValue !== undefined ? value === expectedValue : Boolean(value);
        });
  }

  getDynamicValue<T extends Record<string, T>>(obj: T, path: string): T|undefined {
    if (!obj || !path) return undefined;
  
    const pathParts = path.split('.');
  
    return pathParts.reduce((acc, key) => {
      if (Array.isArray(acc))
        return acc[parseInt(key, 10)]

      if (acc && acc[key] !== undefined)
        return acc[key]
    }, obj);
  }

  setDynamicValue<T extends Record<string, unknown>>(row: T, path: string, value: unknown): void {
    const keys = path.split('.');
    let result: Record<string, unknown> = row;
  
    // Iterate over the keys to access the nested property
    for (let i = 0; i < keys.length - 1; i++) {
      // Access the property dynamically and check if it's valid
      if (result[keys[i]] && typeof result[keys[i]] === 'object') {
        result = result[keys[i]] as Record<string, unknown>; // Update `result` as a nested object
      } else {
        return; // If any key doesn't exist or isn't an object, exit
      }
    }
  
    // Finally, set the value of the last property in the path
    result[keys[keys.length - 1]] = value;

    this.view.actionRowChangeTriggered.emit(this.view.data);
  }

  toggleChange({ checked }: InputSwitchChangeEvent, row: T): void {
    this.view.actionToggleTriggered.emit({ checked, data: row });
  }

  checkboxChange({ checked }: CheckboxChangeEvent, value: string, row: T): void {
    this.view.actionCheckboxTriggered.emit({ checked, value, data: row });
  }

  toggleRow(rowData: T): void {
    const key = rowData[this.view.selectionKey as keyof T];
    this.view.expandedRowKeys = { [key as string]: !this.view.expandedRowKeys[key as string] };
  }

  rowDataExpanded(rowData: Z[]): Z[] {
    return [...rowData];
  }

  onPageChange(event:TablePageEvent): void {
    this.view.actionPageChange.emit((event.first/this.view.rows)+1)
  }

  downloadElement(element: T|Z): void {
    this.view.actionDownloadTriggered.emit(element);
  }

  isObservable(data: unknown): data is Observable<T[]> {
    return data instanceof Observable;
  }
}
